<groundworks-modal>
  <groundworks-modal-header
    [title]="modalTitle()"
    [isCancellable]="true"
    (cancelModal)="cancel()"
  />
  <groundworks-modal-body>
    <div class="d-flex justify-content-end gap-3 mb-3 small text-muted">
      <button
        type="button"
        class="btn btn-link p-0 text-muted text-decoration-none"
        [disabled]="!canGoPrevious()"
        aria-label="Previous phase"
        (click)="goToPreviousPhase()"
      >
        <i class="bi bi-chevron-left"></i> Previous
      </button>
      <button
        type="button"
        class="btn btn-link p-0 text-muted text-decoration-none"
        [disabled]="!canGoNext()"
        aria-label="Next phase"
        (click)="goToNextPhase()"
      >
        Next <i class="bi bi-chevron-right"></i>
      </button>
    </div>

    <div class="permit-phase-wrapper">
    @if (currentPhase() === ProgramDetails) {
    <div class="row g-4">
      <div class="col-md-4">
        <div class="mb-3">
          <label for="permitProgram" class="form-label">Permit / Program</label>
          <app-multi-permit-type-selector
            [selectedIds]="permitTypeIds()"
            (selectionChange)="permitTypeIds.set($event)"
          />
        </div>
        <div class="mb-3">
          <label for="countyTownship" class="form-label">County / Township</label>
          <input
            id="countyTownship"
            type="text"
            maxlength="200"
            class="form-control"
            [ngModel]="countyTownship()"
            (ngModelChange)="countyTownship.set($event)"
            placeholder="Enter county / township."
          />
        </div>
      </div>

      <div class="col-md-4">
        <div class="mb-3">
          <label for="permitNumber" class="form-label">Permit Number</label>
          <input
            id="permitNumber"
            type="text"
            maxlength="100"
            class="form-control"
            [ngModel]="permitNumber()"
            (ngModelChange)="permitNumber.set($event)"
            placeholder="Enter permit number."
          />
        </div>
        <div class="mb-3">
          <label for="permitName" class="form-label">Permit Name</label>
          <input
            id="permitName"
            type="text"
            maxlength="200"
            class="form-control"
            [ngModel]="permitName()"
            (ngModelChange)="permitName.set($event)"
            placeholder="Enter permit name."
          />
        </div>
      </div>

      <div class="col-md-4">
        <div class="mb-3">
          <label for="nextActionDate" class="form-label">Next Action Date</label>
          <div class="position-relative">
            <input
              id="nextActionDate"
              type="text"
              readonly
              class="form-control pe-4"
              [value]="formatDateForDisplay(nextActionDate())"
              placeholder="Select date"
              [style.cursor]="'pointer'"
              (click)="toggleNextActionDatePicker()"
            />
            <button
              type="button"
              class="btn btn-sm p-0 border-0 position-absolute top-50 end-0 translate-middle-y me-3"
              aria-label="Toggle next action date picker"
              (click)="toggleNextActionDatePicker()"
            >
              <i class="bi bi-calendar-check"></i>
            </button>
            @if (showNextActionDatePicker()) {
              <div class="position-absolute top-100 start-0 mt-1 bg-white border rounded shadow-lg z-1000">
                <groundworks-date-picker
                  [isRangeSelection]="false"
                  [displayMonths]="1"
                  [selectedDate]="nextActionDateAsDate()"
                  (fromDateEvent)="handleNextActionDateSelected($event)"
                />
              </div>
            }
          </div>
        </div>

        <div class="mb-3">
          <label for="permitNotes" class="form-label">Permit Notes</label>
          <textarea
            id="permitNotes"
            class="form-control"
            rows="4"
            [value]="notes()"
            (input)="onNotesInput($event)"
            [attr.maxlength]="maxNotesLength"
            placeholder="Enter permit notes."
          ></textarea>
          <div class="d-flex justify-content-between align-items-baseline mt-1">
            <small class="form-text fst-italic">Previous notes are available in Touchpoints.</small>
            <small class="text-muted" [class.text-danger]="notesCharacterCount() > maxNotesLength">
              {{ notesCharacterCount() }} / {{ maxNotesLength }}
            </small>
          </div>
        </div>

        @if (isPhaseLocked()) {
          <div class="mb-3">
            <div class="form-label">Permit Phase Status</div>
            <div class="d-flex gap-2 align-items-baseline">
              <span class="fw-semibold">{{ phaseCompletionLabel() }}:</span>
              @if (persistedPhase1()?.completedByName; as name) { <span>{{ name }}</span> }
              @if (persistedPhase1()?.completedAtUtc; as completedAt) {
                <span class="text-muted small">
                  {{ completedAt | utcToLocal | date:'h:mm a' }} {{ completedAt | utcToLocal | date:'MM/dd/yyyy' }}
                </span>
              }
            </div>
          </div>
        } @else {
          <fieldset class="mb-3">
            <legend class="form-label">Permit Phase Status</legend>
            <div class="d-flex gap-3">
              <div class="form-check" [title]="phaseResolveDisabledReason() ?? ''">
                <input
                  id="phaseStatusCompleted"
                  class="form-check-input"
                  type="radio"
                  name="permitPhaseStatus"
                  [checked]="phaseStatus() === Completed"
                  [disabled]="!canResolveCurrentPhase()"
                  (click)="togglePhaseStatusRadio(Completed)"
                />
                <label class="form-check-label" for="phaseStatusCompleted">Completed</label>
              </div>
              <div class="form-check" [title]="phaseResolveDisabledReason() ?? ''">
                <input
                  id="phaseStatusNotNeeded"
                  class="form-check-input"
                  type="radio"
                  name="permitPhaseStatus"
                  [checked]="phaseStatus() === NotNeeded"
                  [disabled]="!canResolveCurrentPhase()"
                  (click)="togglePhaseStatusRadio(NotNeeded)"
                />
                <label class="form-check-label" for="phaseStatusNotNeeded">Not Needed</label>
              </div>
            </div>
          </fieldset>
        }
      </div>
    </div>

    } @else if (currentPhase() === Preparation) {
    <div class="row g-4">
      <!-- Column 1: Intake / CFI / Admin / Soil / Plat / Neighbor -->
      <div class="col-md-4">
        <div class="mb-3">
          <label for="engineeringIntakeProvidedByCfi" class="form-label">Engineering Intake Provided by CFI</label>
          <ng-select
            inputId="engineeringIntakeProvidedByCfi"
            [items]="cfiIntakeOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="engineeringIntakeProvidedByCfi()"
            (ngModelChange)="engineeringIntakeProvidedByCfi.set($event)">
          </ng-select>
        </div>

        <div class="mb-3">
          <label for="cfiRemainingNeeds" class="form-label">CFI Remaining Needs</label>
          <input
            id="cfiRemainingNeeds"
            type="text"
            maxlength="1000"
            class="form-control"
            [ngModel]="cfiRemainingNeeds()"
            (ngModelChange)="cfiRemainingNeeds.set($event)"
            placeholder="Enter CFI remaining needs."
          />
        </div>

        <div class="mb-3">
          <label for="assignedAdminCad" class="form-label">Assigned Admin/CAD</label>
          <input
            id="assignedAdminCad"
            type="text"
            maxlength="200"
            class="form-control"
            [ngModel]="assignedAdminCad()"
            (ngModelChange)="assignedAdminCad.set($event)"
            placeholder="Enter assigned admin/CAD."
          />
        </div>

        <div class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <label for="soilReport" class="form-label mb-0">Soil Report</label>
            <input
              id="soilReportReminder"
              type="checkbox"
              class="form-check-input ms-0 mt-0 flex-shrink-0"
              aria-label="Soil Report reminder"
              [checked]="reminders().soilReport"
              (change)="toggleReminder('soilReport', $any($event.target).checked)"
            />
          </div>
          <ng-select
            inputId="soilReport"
            [items]="preparationItemStatusOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="soilReport()"
            (ngModelChange)="soilReport.set($event)">
          </ng-select>
        </div>

        <div class="mb-3">
          <label for="soilReportExpectedDate" class="form-label">Soil Report Expected Date of Return</label>
          <div class="position-relative">
            <input
              id="soilReportExpectedDate"
              type="text"
              readonly
              class="form-control pe-4"
              [value]="formatDateForDisplay(soilReportExpectedReturnDate())"
              placeholder="Select date"
              [style.cursor]="'pointer'"
              (click)="toggleSoilReportExpectedDatePicker()"
            />
            <button
              type="button"
              class="btn btn-sm p-0 border-0 position-absolute top-50 end-0 translate-middle-y me-3"
              aria-label="Toggle soil report expected date picker"
              (click)="toggleSoilReportExpectedDatePicker()"
            >
              <i class="bi bi-calendar-check"></i>
            </button>
            @if (showSoilReportExpectedDatePicker()) {
              <div class="position-absolute top-100 start-0 mt-1 bg-white border rounded shadow-lg z-1000">
                <groundworks-date-picker
                  [isRangeSelection]="false"
                  [displayMonths]="1"
                  [selectedDate]="soilReportExpectedReturnDateAsDate()"
                  (fromDateEvent)="handleSoilReportExpectedDateSelected($event)"
                />
              </div>
            }
          </div>
        </div>

        <div class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <label for="platSurveyIlc" class="form-label mb-0">Plat/Survey/ILC</label>
            <input
              id="platSurveyIlcReminder"
              type="checkbox"
              class="form-check-input ms-0 mt-0 flex-shrink-0"
              aria-label="Plat/Survey/ILC reminder"
              [checked]="reminders().platSurveyIlc"
              (change)="toggleReminder('platSurveyIlc', $any($event.target).checked)"
            />
          </div>
          <ng-select
            inputId="platSurveyIlc"
            [items]="preparationItemStatusOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="platSurveyIlc()"
            (ngModelChange)="platSurveyIlc.set($event)">
          </ng-select>
        </div>

        <div class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <label for="neighborNotification" class="form-label mb-0">Neighbor Notification</label>
            <input
              id="neighborNotificationReminder"
              type="checkbox"
              class="form-check-input ms-0 mt-0 flex-shrink-0"
              aria-label="Neighbor Notification reminder"
              [checked]="reminders().neighborNotification"
              (change)="toggleReminder('neighborNotification', $any($event.target).checked)"
            />
          </div>
          <ng-select
            inputId="neighborNotification"
            [items]="preparationItemStatusOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="neighborNotification()"
            (ngModelChange)="neighborNotification.set($event)">
          </ng-select>
        </div>
      </div>

      <!-- Column 2: Test Pit / Electrical / Plumbing -->
      <div class="col-md-4">
        <div class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <label for="testPitTestPier" class="form-label mb-0">Test Pit / Test Pier</label>
            <input
              id="testPitTestPierReminder"
              type="checkbox"
              class="form-check-input ms-0 mt-0 flex-shrink-0"
              aria-label="Test Pit / Test Pier reminder"
              [checked]="reminders().testPitTestPier"
              (change)="toggleReminder('testPitTestPier', $any($event.target).checked)"
            />
          </div>
          <ng-select
            inputId="testPitTestPier"
            [items]="preparationItemStatusOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="testPitTestPier()"
            (ngModelChange)="testPitTestPier.set($event)">
          </ng-select>
        </div>

        <div class="mb-3">
          <label for="testPitDate" class="form-label">Date of Test Pit</label>
          <div class="position-relative">
            <input
              id="testPitDate"
              type="text"
              readonly
              class="form-control pe-4"
              [value]="formatDateForDisplay(testPitDate())"
              placeholder="Select date"
              [style.cursor]="'pointer'"
              (click)="toggleTestPitDatePicker()"
            />
            <button
              type="button"
              class="btn btn-sm p-0 border-0 position-absolute top-50 end-0 translate-middle-y me-3"
              aria-label="Toggle test pit date picker"
              (click)="toggleTestPitDatePicker()"
            >
              <i class="bi bi-calendar-check"></i>
            </button>
            @if (showTestPitDatePicker()) {
              <div class="position-absolute top-100 start-0 mt-1 bg-white border rounded shadow-lg z-1000">
                <groundworks-date-picker
                  [isRangeSelection]="false"
                  [displayMonths]="1"
                  [selectedDate]="testPitDateAsDate()"
                  (fromDateEvent)="handleTestPitDateSelected($event)"
                />
              </div>
            }
          </div>
        </div>

        <div class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <label for="electricalPermitNeeded" class="form-label mb-0">Electrical Permit Needed</label>
            <input
              id="electricalPermitNeededReminder"
              type="checkbox"
              class="form-check-input ms-0 mt-0 flex-shrink-0"
              aria-label="Electrical Permit Needed reminder"
              [checked]="reminders().electricalPermitNeeded"
              (change)="toggleReminder('electricalPermitNeeded', $any($event.target).checked)"
            />
          </div>
          <ng-select
            inputId="electricalPermitNeeded"
            [items]="subcontractedPermitStatusOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="electricalPermitNeeded()"
            (ngModelChange)="electricalPermitNeeded.set($event)">
          </ng-select>
        </div>

        <div class="mb-3">
          <label for="electricalPermitNumber" class="form-label">Electrical Permit Number</label>
          <input
            id="electricalPermitNumber"
            type="text"
            maxlength="100"
            class="form-control"
            [ngModel]="electricalPermitNumber()"
            (ngModelChange)="electricalPermitNumber.set($event)"
            placeholder="Enter electrical permit number."
          />
        </div>

        <div class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <label for="plumbingPermitNeeded" class="form-label mb-0">Plumbing Permit Needed</label>
            <input
              id="plumbingPermitNeededReminder"
              type="checkbox"
              class="form-check-input ms-0 mt-0 flex-shrink-0"
              aria-label="Plumbing Permit Needed reminder"
              [checked]="reminders().plumbingPermitNeeded"
              (change)="toggleReminder('plumbingPermitNeeded', $any($event.target).checked)"
            />
          </div>
          <ng-select
            inputId="plumbingPermitNeeded"
            [items]="subcontractedPermitStatusOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="plumbingPermitNeeded()"
            (ngModelChange)="plumbingPermitNeeded.set($event)">
          </ng-select>
        </div>

        <div class="mb-3">
          <label for="plumbingPermitNumber" class="form-label">Plumbing Permit Number</label>
          <input
            id="plumbingPermitNumber"
            type="text"
            maxlength="100"
            class="form-control"
            [ngModel]="plumbingPermitNumber()"
            (ngModelChange)="plumbingPermitNumber.set($event)"
            placeholder="Enter plumbing permit number."
          />
        </div>
      </div>

      <!-- Column 3: Right rail (Next Action Date, Notes, Phase Status) -->
      <div class="col-md-4">
        <div class="mb-3">
          <label for="phase2NextActionDate" class="form-label">Next Action Date</label>
          <div class="position-relative">
            <input
              id="phase2NextActionDate"
              type="text"
              readonly
              class="form-control pe-4"
              [value]="formatDateForDisplay(phase2NextActionDate())"
              placeholder="Select date"
              [style.cursor]="'pointer'"
              (click)="togglePhase2NextActionDatePicker()"
            />
            <button
              type="button"
              class="btn btn-sm p-0 border-0 position-absolute top-50 end-0 translate-middle-y me-3"
              aria-label="Toggle next action date picker"
              (click)="togglePhase2NextActionDatePicker()"
            >
              <i class="bi bi-calendar-check"></i>
            </button>
            @if (showPhase2NextActionDatePicker()) {
              <div class="position-absolute top-100 start-0 mt-1 bg-white border rounded shadow-lg z-1000">
                <groundworks-date-picker
                  [isRangeSelection]="false"
                  [displayMonths]="1"
                  [selectedDate]="phase2NextActionDateAsDate()"
                  (fromDateEvent)="handlePhase2NextActionDateSelected($event)"
                />
              </div>
            }
          </div>
        </div>

        <div class="mb-3">
          <label for="phase2PermitNotes" class="form-label">Permit Notes</label>
          <textarea
            id="phase2PermitNotes"
            class="form-control"
            rows="6"
            [value]="phase2Notes()"
            (input)="onPhase2NotesInput($event)"
            [attr.maxlength]="maxNotesLength"
            placeholder="Enter permit notes."
          ></textarea>
          <div class="d-flex justify-content-between align-items-baseline mt-1">
            <small class="form-text fst-italic">Please see Touchpoints for note history</small>
            <small class="text-muted" [class.text-danger]="phase2NotesCharacterCount() > maxNotesLength">
              {{ phase2NotesCharacterCount() }} / {{ maxNotesLength }}
            </small>
          </div>
        </div>

        @if (isPhaseLocked()) {
          <div class="mb-3">
            <div class="form-label">Permit Phase Status</div>
            <div class="d-flex gap-2 align-items-baseline">
              <span class="fw-semibold">{{ phaseCompletionLabel() }}:</span>
              @if (persistedCurrentPhase()?.completedByName; as name) { <span>{{ name }}</span> }
              @if (persistedCurrentPhase()?.completedAtUtc; as completedAt) {
                <span class="text-muted small">
                  {{ completedAt | utcToLocal | date:'h:mm a' }} {{ completedAt | utcToLocal | date:'MM/dd/yyyy' }}
                </span>
              }
            </div>
          </div>
        } @else {
          <fieldset class="mb-3">
            <legend class="form-label">Permit Phase Status</legend>
            <div class="d-flex gap-3">
              <div class="form-check" [title]="phaseResolveDisabledReason() ?? ''">
                <input
                  id="phase2StatusCompleted"
                  class="form-check-input"
                  type="radio"
                  name="phase2PermitPhaseStatus"
                  [checked]="phase2Status() === Completed"
                  [disabled]="!canResolveCurrentPhase()"
                  (click)="togglePhase2StatusRadio(Completed)"
                />
                <label class="form-check-label" for="phase2StatusCompleted">Completed</label>
              </div>
              <div class="form-check" [title]="phaseResolveDisabledReason() ?? ''">
                <input
                  id="phase2StatusNotNeeded"
                  class="form-check-input"
                  type="radio"
                  name="phase2PermitPhaseStatus"
                  [checked]="phase2Status() === NotNeeded"
                  [disabled]="!canResolveCurrentPhase()"
                  (click)="togglePhase2StatusRadio(NotNeeded)"
                />
                <label class="form-check-label" for="phase2StatusNotNeeded">Not Needed</label>
              </div>
            </div>
          </fieldset>
        }
      </div>
    </div>
    } @else if (currentPhase() === OpenPermit) {
    <div class="row g-4">
      <!-- Column 1: Mid Inspection (Type + Checkbox), Date, Inspector, Status, Electrical, Plumbing -->
      <div class="col-md-4">
        <div class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <label for="midInspectionType" class="form-label mb-0">Type of Mid Inspection Required</label>
            <input
              id="midInspectionTypeReminder"
              type="checkbox"
              class="form-check-input ms-0 mt-0 flex-shrink-0"
              aria-label="Mid Inspection Required reminder"
              [checked]="reminders().midInspectionType"
              (change)="toggleReminder('midInspectionType', $any($event.target).checked)"
            />
          </div>
          <ng-select
            inputId="midInspectionType"
            [items]="midInspectionTypeOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="midInspectionType()"
            (ngModelChange)="midInspectionType.set($event)">
          </ng-select>
        </div>

        <div class="mb-3">
          <label for="midInspectionDate" class="form-label">Date of Inspection</label>
          <div class="position-relative">
            <input
              id="midInspectionDate"
              type="text"
              readonly
              class="form-control pe-4"
              [value]="formatDateForDisplay(midInspectionDate())"
              placeholder="Select date"
              [style.cursor]="'pointer'"
              (click)="toggleMidInspectionDatePicker()"
            />
            <button
              type="button"
              class="btn btn-sm p-0 border-0 position-absolute top-50 end-0 translate-middle-y me-3"
              aria-label="Toggle mid inspection date picker"
              (click)="toggleMidInspectionDatePicker()"
            >
              <i class="bi bi-calendar-check"></i>
            </button>
            @if (showMidInspectionDatePicker()) {
              <div class="position-absolute top-100 start-0 mt-1 bg-white border rounded shadow-lg z-1000">
                <groundworks-date-picker
                  [isRangeSelection]="false"
                  [displayMonths]="1"
                  [selectedDate]="midInspectionDateAsDate()"
                  (fromDateEvent)="handleMidInspectionDateSelected($event)"
                />
              </div>
            }
          </div>
        </div>

        <div class="mb-3">
          <label for="midInspectionInspector" class="form-label">Name / Phone Number of Inspector</label>
          <input
            id="midInspectionInspector"
            type="text"
            maxlength="500"
            class="form-control"
            [ngModel]="midInspectionInspector()"
            (ngModelChange)="midInspectionInspector.set($event)"
            placeholder="Enter inspector name / phone number."
          />
        </div>

        <div class="mb-3">
          <label for="midInspectionStatus" class="form-label">Mid Inspection Status</label>
          <ng-select
            inputId="midInspectionStatus"
            [items]="midInspectionStatusOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="midInspectionStatus()"
            (ngModelChange)="midInspectionStatus.set($event)">
          </ng-select>
        </div>

        <div class="mb-3">
          <label for="electricalPermitStatus" class="form-label">Electrical Permit Status</label>
          <ng-select
            inputId="electricalPermitStatus"
            [items]="externalPermitStatusOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="electricalPermitStatus()"
            (ngModelChange)="electricalPermitStatus.set($event)">
          </ng-select>
        </div>

        <div class="mb-3">
          <label for="plumbingPermitStatus" class="form-label">Plumbing Permit Status</label>
          <ng-select
            inputId="plumbingPermitStatus"
            [items]="externalPermitStatusOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="plumbingPermitStatus()"
            (ngModelChange)="plumbingPermitStatus.set($event)">
          </ng-select>
        </div>
      </div>

      <!-- Column 2: Permit As-Build (+chk), Final Inspection (Type+chk, Date, Inspector, Status), Inspection Failure -->
      <div class="col-md-4">
        <div class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <label for="permitAsBuildRequired" class="form-label mb-0">Permit "As-Build" Required</label>
            <input
              id="permitAsBuildRequiredReminder"
              type="checkbox"
              class="form-check-input ms-0 mt-0 flex-shrink-0"
              aria-label="Permit As-Build Required reminder"
              [checked]="reminders().permitAsBuildRequired"
              (change)="toggleReminder('permitAsBuildRequired', $any($event.target).checked)"
            />
          </div>
          <ng-select
            inputId="permitAsBuildRequired"
            [items]="permitAsBuildRequiredOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="permitAsBuildRequired()"
            (ngModelChange)="permitAsBuildRequired.set($event)">
          </ng-select>
        </div>

        <div class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <label for="finalInspectionType" class="form-label mb-0">Type of Final Inspection Required</label>
            <input
              id="finalInspectionTypeReminder"
              type="checkbox"
              class="form-check-input ms-0 mt-0 flex-shrink-0"
              aria-label="Final Inspection Required reminder"
              [checked]="reminders().finalInspectionType"
              (change)="toggleReminder('finalInspectionType', $any($event.target).checked)"
            />
          </div>
          <ng-select
            inputId="finalInspectionType"
            [items]="finalInspectionTypeOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="finalInspectionType()"
            (ngModelChange)="finalInspectionType.set($event)">
          </ng-select>
        </div>

        <div class="mb-3">
          <label for="finalInspectionDate" class="form-label">Date of Final Inspection</label>
          <div class="position-relative">
            <input
              id="finalInspectionDate"
              type="text"
              readonly
              class="form-control pe-4"
              [value]="formatDateForDisplay(finalInspectionDate())"
              placeholder="Select date"
              [style.cursor]="'pointer'"
              (click)="toggleFinalInspectionDatePicker()"
            />
            <button
              type="button"
              class="btn btn-sm p-0 border-0 position-absolute top-50 end-0 translate-middle-y me-3"
              aria-label="Toggle final inspection date picker"
              (click)="toggleFinalInspectionDatePicker()"
            >
              <i class="bi bi-calendar-check"></i>
            </button>
            @if (showFinalInspectionDatePicker()) {
              <div class="position-absolute top-100 start-0 mt-1 bg-white border rounded shadow-lg z-1000">
                <groundworks-date-picker
                  [isRangeSelection]="false"
                  [displayMonths]="1"
                  [selectedDate]="finalInspectionDateAsDate()"
                  (fromDateEvent)="handleFinalInspectionDateSelected($event)"
                />
              </div>
            }
          </div>
        </div>

        <div class="mb-3">
          <label for="finalInspectionInspector" class="form-label">Name / Phone Number of Inspector</label>
          <input
            id="finalInspectionInspector"
            type="text"
            maxlength="500"
            class="form-control"
            [ngModel]="finalInspectionInspector()"
            (ngModelChange)="finalInspectionInspector.set($event)"
            placeholder="Enter inspector name / phone number."
          />
        </div>

        <div class="mb-3">
          <label for="finalInspectionStatus" class="form-label">Final Inspection Status</label>
          <ng-select
            inputId="finalInspectionStatus"
            [items]="finalInspectionStatusOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="finalInspectionStatus()"
            (ngModelChange)="finalInspectionStatus.set($event)">
          </ng-select>
        </div>

        <div class="mb-3">
          <label for="inspectionFailureReason" class="form-label">Inspection Failure Reason</label>
          <input
            id="inspectionFailureReason"
            type="text"
            maxlength="1000"
            class="form-control"
            [ngModel]="inspectionFailureReason()"
            (ngModelChange)="inspectionFailureReason.set($event)"
            placeholder="Enter inspection failure reason."
          />
        </div>
      </div>

      <!-- Column 3: Right rail -->
      <div class="col-md-4">
        <div class="mb-3">
          <label for="phase5NextActionDate" class="form-label">Next Action Date</label>
          <div class="position-relative">
            <input
              id="phase5NextActionDate"
              type="text"
              readonly
              class="form-control pe-4"
              [value]="formatDateForDisplay(phase5NextActionDate())"
              placeholder="Select date"
              [style.cursor]="'pointer'"
              (click)="togglePhase5NextActionDatePicker()"
            />
            <button
              type="button"
              class="btn btn-sm p-0 border-0 position-absolute top-50 end-0 translate-middle-y me-3"
              aria-label="Toggle next action date picker"
              (click)="togglePhase5NextActionDatePicker()"
            >
              <i class="bi bi-calendar-check"></i>
            </button>
            @if (showPhase5NextActionDatePicker()) {
              <div class="position-absolute top-100 start-0 mt-1 bg-white border rounded shadow-lg z-1000">
                <groundworks-date-picker
                  [isRangeSelection]="false"
                  [displayMonths]="1"
                  [selectedDate]="phase5NextActionDateAsDate()"
                  (fromDateEvent)="handlePhase5NextActionDateSelected($event)"
                />
              </div>
            }
          </div>
        </div>

        <div class="mb-3">
          <label for="phase5PermitNotes" class="form-label">Permit Notes</label>
          <textarea
            id="phase5PermitNotes"
            class="form-control"
            rows="6"
            [value]="phase5Notes()"
            (input)="onPhase5NotesInput($event)"
            [attr.maxlength]="maxNotesLength"
            placeholder="Enter permit notes."
          ></textarea>
          <div class="d-flex justify-content-between align-items-baseline mt-1">
            <small class="form-text fst-italic">Previous notes are available in Touchpoints.</small>
            <small class="text-muted" [class.text-danger]="phase5NotesCharacterCount() > maxNotesLength">
              {{ phase5NotesCharacterCount() }} / {{ maxNotesLength }}
            </small>
          </div>
        </div>

        @if (isPhaseLocked()) {
          <div class="mb-3">
            <div class="form-label">Permit Phase Status</div>
            <div class="d-flex gap-2 align-items-baseline">
              <span class="fw-semibold">{{ phaseCompletionLabel() }}:</span>
              @if (persistedCurrentPhase()?.completedByName; as name) { <span>{{ name }}</span> }
              @if (persistedCurrentPhase()?.completedAtUtc; as completedAt) {
                <span class="text-muted small">
                  {{ completedAt | utcToLocal | date:'h:mm a' }} {{ completedAt | utcToLocal | date:'MM/dd/yyyy' }}
                </span>
              }
            </div>
          </div>
        } @else {
          <fieldset class="mb-3">
            <legend class="form-label">Permit Phase Status</legend>
            <div class="d-flex gap-3">
              <div class="form-check" [title]="phaseResolveDisabledReason() ?? ''">
                <input
                  id="phase5StatusCompleted"
                  class="form-check-input"
                  type="radio"
                  name="phase5PermitPhaseStatus"
                  [checked]="phase5Status() === Completed"
                  [disabled]="!canResolveCurrentPhase()"
                  (click)="togglePhase5StatusRadio(Completed)"
                />
                <label class="form-check-label" for="phase5StatusCompleted">Completed</label>
              </div>
              <div class="form-check" [title]="phaseResolveDisabledReason() ?? ''">
                <input
                  id="phase5StatusNotNeeded"
                  class="form-check-input"
                  type="radio"
                  name="phase5PermitPhaseStatus"
                  [checked]="phase5Status() === NotNeeded"
                  [disabled]="!canResolveCurrentPhase()"
                  (click)="togglePhase5StatusRadio(NotNeeded)"
                />
                <label class="form-check-label" for="phase5StatusNotNeeded">Not Needed</label>
              </div>
            </div>
          </fieldset>
        }
      </div>
    </div>
    } @else if (currentPhase() === Building) {
    <div class="row g-4">
      <!-- Column 1: Permit App Sent / Expected Return / Permit Status -->
      <div class="col-md-4">
        <div class="mb-3">
          <label for="permitAppSentDate" class="form-label">Permit App Sent to Building Department</label>
          <div class="position-relative">
            <input
              id="permitAppSentDate"
              type="text"
              readonly
              class="form-control pe-4"
              [value]="formatDateForDisplay(permitAppSentDate())"
              placeholder="Select date"
              [style.cursor]="'pointer'"
              (click)="togglePermitAppSentDatePicker()"
            />
            <button
              type="button"
              class="btn btn-sm p-0 border-0 position-absolute top-50 end-0 translate-middle-y me-3"
              aria-label="Toggle permit app sent date picker"
              (click)="togglePermitAppSentDatePicker()"
            >
              <i class="bi bi-calendar-check"></i>
            </button>
            @if (showPermitAppSentDatePicker()) {
              <div class="position-absolute top-100 start-0 mt-1 bg-white border rounded shadow-lg z-1000">
                <groundworks-date-picker
                  [isRangeSelection]="false"
                  [displayMonths]="1"
                  [selectedDate]="permitAppSentDateAsDate()"
                  (fromDateEvent)="handlePermitAppSentDateSelected($event)"
                />
              </div>
            }
          </div>
        </div>

        <div class="mb-3">
          <label for="buildingExpectedReturnDate" class="form-label">Expected Date of Return</label>
          <div class="position-relative">
            <input
              id="buildingExpectedReturnDate"
              type="text"
              readonly
              class="form-control pe-4"
              [value]="formatDateForDisplay(buildingExpectedReturnDate())"
              placeholder="Select date"
              [style.cursor]="'pointer'"
              (click)="toggleBuildingExpectedReturnDatePicker()"
            />
            <button
              type="button"
              class="btn btn-sm p-0 border-0 position-absolute top-50 end-0 translate-middle-y me-3"
              aria-label="Toggle building expected return date picker"
              (click)="toggleBuildingExpectedReturnDatePicker()"
            >
              <i class="bi bi-calendar-check"></i>
            </button>
            @if (showBuildingExpectedReturnDatePicker()) {
              <div class="position-absolute top-100 start-0 mt-1 bg-white border rounded shadow-lg z-1000">
                <groundworks-date-picker
                  [isRangeSelection]="false"
                  [displayMonths]="1"
                  [selectedDate]="buildingExpectedReturnDateAsDate()"
                  (fromDateEvent)="handleBuildingExpectedReturnDateSelected($event)"
                />
              </div>
            }
          </div>
        </div>

        <div class="mb-3">
          <label for="buildingPermitStatus" class="form-label">Permit Status</label>
          <ng-select
            inputId="buildingPermitStatus"
            [items]="buildingPermitStatusOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="buildingPermitStatus()"
            (ngModelChange)="buildingPermitStatus.set($event)">
          </ng-select>
        </div>
      </div>

      <!-- Column 2: Costs / Expiration -->
      <div class="col-md-4">
        <div class="mb-3">
          <label for="costOfPermits" class="form-label">Cost of Permit(s)</label>
          <div class="input-group">
            <span class="input-group-text">$</span>
            <input
              id="costOfPermits"
              type="text"
              inputmode="decimal"
              class="form-control text-end"
              placeholder="0.00"
              [ngModel]="costOfPermitsDisplay()"
              (ngModelChange)="costOfPermitsDisplay.set($event)"
              (blur)="onCostOfPermitsBlur()"
            />
          </div>
        </div>

        <div class="mb-3">
          <label for="costOfThirdPartyDocsServices" class="form-label">Cost of 3rd Party Docs/Services</label>
          <div class="input-group">
            <span class="input-group-text">$</span>
            <input
              id="costOfThirdPartyDocsServices"
              type="text"
              inputmode="decimal"
              class="form-control text-end"
              placeholder="0.00"
              [ngModel]="costOfThirdPartyDocsServicesDisplay()"
              (ngModelChange)="costOfThirdPartyDocsServicesDisplay.set($event)"
              (blur)="onCostOfThirdPartyDocsServicesBlur()"
            />
          </div>
        </div>

        <div class="mb-3">
          <label for="permitExpirationDate" class="form-label">Permit Expiration Date</label>
          <div class="position-relative">
            <input
              id="permitExpirationDate"
              type="text"
              readonly
              class="form-control pe-4"
              [value]="formatDateForDisplay(permitExpirationDate())"
              placeholder="Select date"
              [style.cursor]="'pointer'"
              (click)="togglePermitExpirationDatePicker()"
            />
            <button
              type="button"
              class="btn btn-sm p-0 border-0 position-absolute top-50 end-0 translate-middle-y me-3"
              aria-label="Toggle permit expiration date picker"
              (click)="togglePermitExpirationDatePicker()"
            >
              <i class="bi bi-calendar-check"></i>
            </button>
            @if (showPermitExpirationDatePicker()) {
              <div class="position-absolute top-100 start-0 mt-1 bg-white border rounded shadow-lg z-1000">
                <groundworks-date-picker
                  [isRangeSelection]="false"
                  [displayMonths]="1"
                  [selectedDate]="permitExpirationDateAsDate()"
                  (fromDateEvent)="handlePermitExpirationDateSelected($event)"
                />
              </div>
            }
          </div>
        </div>
      </div>

      <!-- Column 3: Right rail (Next Action Date, Notes, Phase Status) -->
      <div class="col-md-4">
        <div class="mb-3">
          <label for="phase4NextActionDate" class="form-label">Next Action Date</label>
          <div class="position-relative">
            <input
              id="phase4NextActionDate"
              type="text"
              readonly
              class="form-control pe-4"
              [value]="formatDateForDisplay(phase4NextActionDate())"
              placeholder="Select date"
              [style.cursor]="'pointer'"
              (click)="togglePhase4NextActionDatePicker()"
            />
            <button
              type="button"
              class="btn btn-sm p-0 border-0 position-absolute top-50 end-0 translate-middle-y me-3"
              aria-label="Toggle next action date picker"
              (click)="togglePhase4NextActionDatePicker()"
            >
              <i class="bi bi-calendar-check"></i>
            </button>
            @if (showPhase4NextActionDatePicker()) {
              <div class="position-absolute top-100 start-0 mt-1 bg-white border rounded shadow-lg z-1000">
                <groundworks-date-picker
                  [isRangeSelection]="false"
                  [displayMonths]="1"
                  [selectedDate]="phase4NextActionDateAsDate()"
                  (fromDateEvent)="handlePhase4NextActionDateSelected($event)"
                />
              </div>
            }
          </div>
        </div>

        <div class="mb-3">
          <label for="phase4PermitNotes" class="form-label">Permit Notes</label>
          <textarea
            id="phase4PermitNotes"
            class="form-control"
            rows="6"
            [value]="phase4Notes()"
            (input)="onPhase4NotesInput($event)"
            [attr.maxlength]="maxNotesLength"
            placeholder="Enter permit notes."
          ></textarea>
          <div class="d-flex justify-content-between align-items-baseline mt-1">
            <small class="form-text fst-italic">Previous notes are available in Touchpoints.</small>
            <small class="text-muted" [class.text-danger]="phase4NotesCharacterCount() > maxNotesLength">
              {{ phase4NotesCharacterCount() }} / {{ maxNotesLength }}
            </small>
          </div>
        </div>

        @if (isPhaseLocked()) {
          <div class="mb-3">
            <div class="form-label">Permit Phase Status</div>
            <div class="d-flex gap-2 align-items-baseline">
              <span class="fw-semibold">{{ phaseCompletionLabel() }}:</span>
              @if (persistedCurrentPhase()?.completedByName; as name) { <span>{{ name }}</span> }
              @if (persistedCurrentPhase()?.completedAtUtc; as completedAt) {
                <span class="text-muted small">
                  {{ completedAt | utcToLocal | date:'h:mm a' }} {{ completedAt | utcToLocal | date:'MM/dd/yyyy' }}
                </span>
              }
            </div>
          </div>
        } @else {
          <fieldset class="mb-3">
            <legend class="form-label">Permit Phase Status</legend>
            <div class="d-flex gap-3">
              <div class="form-check" [title]="phaseResolveDisabledReason() ?? ''">
                <input
                  id="phase4StatusCompleted"
                  class="form-check-input"
                  type="radio"
                  name="phase4PermitPhaseStatus"
                  [checked]="phase4Status() === Completed"
                  [disabled]="!canResolveCurrentPhase()"
                  (click)="togglePhase4StatusRadio(Completed)"
                />
                <label class="form-check-label" for="phase4StatusCompleted">Completed</label>
              </div>
              <div class="form-check" [title]="phaseResolveDisabledReason() ?? ''">
                <input
                  id="phase4StatusNotNeeded"
                  class="form-check-input"
                  type="radio"
                  name="phase4PermitPhaseStatus"
                  [checked]="phase4Status() === NotNeeded"
                  [disabled]="!canResolveCurrentPhase()"
                  (click)="togglePhase4StatusRadio(NotNeeded)"
                />
                <label class="form-check-label" for="phase4StatusNotNeeded">Not Needed</label>
              </div>
            </div>
          </fieldset>
        }
      </div>
    </div>
    } @else if (currentPhase() === Engineering) {
    <div class="row g-4">
      <!-- Column 1: Engineer of Record / Expected Return / Doc Status / Soil / Plat / Test Pit -->
      <div class="col-md-4">
        <div class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <label for="engineerOfRecord" class="form-label mb-0">Engineer of Record</label>
            <input
              id="engineerOfRecordReminder"
              type="checkbox"
              class="form-check-input ms-0 mt-0 flex-shrink-0"
              aria-label="Engineer of Record reminder"
              [checked]="reminders().engineerOfRecord"
              (change)="toggleReminder('engineerOfRecord', $any($event.target).checked)"
            />
          </div>
          <ng-select
            inputId="engineerOfRecord"
            [items]="engineerOfRecordUsers()"
            bindLabel="name"
            bindValue="id"
            placeholder="Example"
            [clearable]="true"
            [ngModel]="engineerOfRecordUserId()"
            (ngModelChange)="engineerOfRecordUserId.set($event)">
          </ng-select>
          @if (engineerOfRecordLoadError(); as err) {
            <div class="text-danger small mt-1" role="alert">{{ err }}</div>
          }
        </div>

        <div class="mb-3">
          <label for="expectedEngineerReturnDate" class="form-label">Expected Engineer Return Date</label>
          <div class="position-relative">
            <input
              id="expectedEngineerReturnDate"
              type="text"
              readonly
              class="form-control pe-4"
              [value]="formatDateForDisplay(expectedEngineerReturnDate())"
              placeholder="Select date"
              [style.cursor]="'pointer'"
              (click)="toggleExpectedEngineerReturnDatePicker()"
            />
            <button
              type="button"
              class="btn btn-sm p-0 border-0 position-absolute top-50 end-0 translate-middle-y me-3"
              aria-label="Toggle expected engineer return date picker"
              (click)="toggleExpectedEngineerReturnDatePicker()"
            >
              <i class="bi bi-calendar-check"></i>
            </button>
            @if (showExpectedEngineerReturnDatePicker()) {
              <div class="position-absolute top-100 start-0 mt-1 bg-white border rounded shadow-lg z-1000">
                <groundworks-date-picker
                  [isRangeSelection]="false"
                  [displayMonths]="1"
                  [selectedDate]="expectedEngineerReturnDateAsDate()"
                  (fromDateEvent)="handleExpectedEngineerReturnDateSelected($event)"
                />
              </div>
            }
          </div>
        </div>

        <div class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <label for="engineeringDocStatus" class="form-label mb-0">Engineering Doc Status</label>
            <input
              id="engineeringDocStatusReminder"
              type="checkbox"
              class="form-check-input ms-0 mt-0 flex-shrink-0"
              aria-label="Engineering Doc Status reminder"
              [checked]="reminders().engineeringDocStatus"
              (change)="toggleReminder('engineeringDocStatus', $any($event.target).checked)"
            />
          </div>
          <ng-select
            inputId="engineeringDocStatus"
            [items]="engineeringDocStatusOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="engineeringDocStatus()"
            (ngModelChange)="engineeringDocStatus.set($event)">
          </ng-select>
        </div>

        <div class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <label for="engSoilReport" class="form-label mb-0">Soil Report</label>
            <input
              id="engSoilReportReminder"
              type="checkbox"
              class="form-check-input ms-0 mt-0 flex-shrink-0"
              aria-label="Soil Report reminder"
              [checked]="reminders().soilReport"
              (change)="toggleReminder('soilReport', $any($event.target).checked)"
            />
          </div>
          <ng-select
            inputId="engSoilReport"
            [items]="preparationItemStatusOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="soilReport()"
            (ngModelChange)="soilReport.set($event)">
          </ng-select>
        </div>

        <div class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <label for="engPlatSurveyIlc" class="form-label mb-0">Plat/Survey/ILC</label>
            <input
              id="engPlatSurveyIlcReminder"
              type="checkbox"
              class="form-check-input ms-0 mt-0 flex-shrink-0"
              aria-label="Plat/Survey/ILC reminder"
              [checked]="reminders().platSurveyIlc"
              (change)="toggleReminder('platSurveyIlc', $any($event.target).checked)"
            />
          </div>
          <ng-select
            inputId="engPlatSurveyIlc"
            [items]="preparationItemStatusOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="platSurveyIlc()"
            (ngModelChange)="platSurveyIlc.set($event)">
          </ng-select>
        </div>

        <div class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <label for="engTestPitTestPier" class="form-label mb-0">Test Pit / Test Pier</label>
            <input
              id="engTestPitTestPierReminder"
              type="checkbox"
              class="form-check-input ms-0 mt-0 flex-shrink-0"
              aria-label="Test Pit / Test Pier reminder"
              [checked]="reminders().testPitTestPier"
              (change)="toggleReminder('testPitTestPier', $any($event.target).checked)"
            />
          </div>
          <ng-select
            inputId="engTestPitTestPier"
            [items]="preparationItemStatusOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="testPitTestPier()"
            (ngModelChange)="testPitTestPier.set($event)">
          </ng-select>
        </div>
      </div>

      <!-- Column 2: 3rd Party Inspections / Inspector / Signed Stmt / Pre-Con Meeting -->
      <div class="col-md-4">
        <div class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <label for="thirdPartyInspections" class="form-label mb-0">3rd Party Inspections</label>
            <input
              id="thirdPartyInspectionsReminder"
              type="checkbox"
              class="form-check-input ms-0 mt-0 flex-shrink-0"
              aria-label="3rd Party Inspections reminder"
              [checked]="reminders().thirdPartyInspections"
              (change)="toggleReminder('thirdPartyInspections', $any($event.target).checked)"
            />
          </div>
          <ng-select
            inputId="thirdPartyInspections"
            [items]="thirdPartyInspectionsOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="thirdPartyInspections()"
            (ngModelChange)="thirdPartyInspections.set($event)">
          </ng-select>
        </div>

        <div class="mb-3">
          <label for="thirdPartyInspector" class="form-label">3rd Party Inspector</label>
          <input
            id="thirdPartyInspector"
            type="text"
            maxlength="500"
            class="form-control"
            [ngModel]="thirdPartyInspector()"
            (ngModelChange)="thirdPartyInspector.set($event)"
            placeholder="Enter 3rd party inspector."
          />
        </div>

        <div class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <label for="signedStatementOfSpecialInspection" class="form-label mb-0">Signed Statement of Special Inspection</label>
            <input
              id="signedStatementOfSpecialInspectionReminder"
              type="checkbox"
              class="form-check-input ms-0 mt-0 flex-shrink-0"
              aria-label="Signed Statement of Special Inspection reminder"
              [checked]="reminders().signedStatementOfSpecialInspection"
              (change)="toggleReminder('signedStatementOfSpecialInspection', $any($event.target).checked)"
            />
          </div>
          <ng-select
            inputId="signedStatementOfSpecialInspection"
            [items]="signedStatementOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="signedStatementOfSpecialInspection()"
            (ngModelChange)="signedStatementOfSpecialInspection.set($event)">
          </ng-select>
        </div>

        <div class="mb-3">
          <div class="d-flex align-items-center gap-2 mb-1">
            <label for="preConstructionMeeting" class="form-label mb-0">Pre-Construction Meeting</label>
            <input
              id="preConstructionMeetingReminder"
              type="checkbox"
              class="form-check-input ms-0 mt-0 flex-shrink-0"
              aria-label="Pre-Construction Meeting reminder"
              [checked]="reminders().preConstructionMeeting"
              (change)="toggleReminder('preConstructionMeeting', $any($event.target).checked)"
            />
          </div>
          <ng-select
            inputId="preConstructionMeeting"
            [items]="preConstructionMeetingOptions"
            bindLabel="label"
            bindValue="value"
            placeholder="Example"
            [clearable]="true"
            [searchable]="false"
            [ngModel]="preConstructionMeeting()"
            (ngModelChange)="preConstructionMeeting.set($event)">
          </ng-select>
        </div>

        <div class="mb-3">
          <label for="preConstructionMeetingDate" class="form-label">Date of Pre-Construction Meeting</label>
          <div class="position-relative">
            <input
              id="preConstructionMeetingDate"
              type="text"
              readonly
              class="form-control pe-4"
              [value]="formatDateForDisplay(preConstructionMeetingDate())"
              placeholder="Select date"
              [style.cursor]="'pointer'"
              (click)="togglePreConstructionMeetingDatePicker()"
            />
            <button
              type="button"
              class="btn btn-sm p-0 border-0 position-absolute top-50 end-0 translate-middle-y me-3"
              aria-label="Toggle pre-construction meeting date picker"
              (click)="togglePreConstructionMeetingDatePicker()"
            >
              <i class="bi bi-calendar-check"></i>
            </button>
            @if (showPreConstructionMeetingDatePicker()) {
              <div class="position-absolute top-100 start-0 mt-1 bg-white border rounded shadow-lg z-1000">
                <groundworks-date-picker
                  [isRangeSelection]="false"
                  [displayMonths]="1"
                  [selectedDate]="preConstructionMeetingDateAsDate()"
                  (fromDateEvent)="handlePreConstructionMeetingDateSelected($event)"
                />
              </div>
            }
          </div>
        </div>
      </div>

      <!-- Column 3: Right rail (Next Action Date, Notes, Phase Status) -->
      <div class="col-md-4">
        <div class="mb-3">
          <label for="phase3NextActionDate" class="form-label">Next Action Date</label>
          <div class="position-relative">
            <input
              id="phase3NextActionDate"
              type="text"
              readonly
              class="form-control pe-4"
              [value]="formatDateForDisplay(phase3NextActionDate())"
              placeholder="Select date"
              [style.cursor]="'pointer'"
              (click)="togglePhase3NextActionDatePicker()"
            />
            <button
              type="button"
              class="btn btn-sm p-0 border-0 position-absolute top-50 end-0 translate-middle-y me-3"
              aria-label="Toggle next action date picker"
              (click)="togglePhase3NextActionDatePicker()"
            >
              <i class="bi bi-calendar-check"></i>
            </button>
            @if (showPhase3NextActionDatePicker()) {
              <div class="position-absolute top-100 start-0 mt-1 bg-white border rounded shadow-lg z-1000">
                <groundworks-date-picker
                  [isRangeSelection]="false"
                  [displayMonths]="1"
                  [selectedDate]="phase3NextActionDateAsDate()"
                  (fromDateEvent)="handlePhase3NextActionDateSelected($event)"
                />
              </div>
            }
          </div>
        </div>

        <div class="mb-3">
          <label for="phase3PermitNotes" class="form-label">Permit Notes</label>
          <textarea
            id="phase3PermitNotes"
            class="form-control"
            rows="6"
            [value]="phase3Notes()"
            (input)="onPhase3NotesInput($event)"
            [attr.maxlength]="maxNotesLength"
            placeholder="Enter permit notes."
          ></textarea>
          <div class="d-flex justify-content-between align-items-baseline mt-1">
            <small class="form-text fst-italic">Previous notes are available in Touchpoints.</small>
            <small class="text-muted" [class.text-danger]="phase3NotesCharacterCount() > maxNotesLength">
              {{ phase3NotesCharacterCount() }} / {{ maxNotesLength }}
            </small>
          </div>
        </div>

        @if (isPhaseLocked()) {
          <div class="mb-3">
            <div class="form-label">Permit Phase Status</div>
            <div class="d-flex gap-2 align-items-baseline">
              <span class="fw-semibold">{{ phaseCompletionLabel() }}:</span>
              @if (persistedCurrentPhase()?.completedByName; as name) { <span>{{ name }}</span> }
              @if (persistedCurrentPhase()?.completedAtUtc; as completedAt) {
                <span class="text-muted small">
                  {{ completedAt | utcToLocal | date:'h:mm a' }} {{ completedAt | utcToLocal | date:'MM/dd/yyyy' }}
                </span>
              }
            </div>
          </div>
        } @else {
          <fieldset class="mb-3">
            <legend class="form-label">Permit Phase Status</legend>
            <div class="d-flex gap-3">
              <div class="form-check" [title]="phaseResolveDisabledReason() ?? ''">
                <input
                  id="phase3StatusCompleted"
                  class="form-check-input"
                  type="radio"
                  name="phase3PermitPhaseStatus"
                  [checked]="phase3Status() === Completed"
                  [disabled]="!canResolveCurrentPhase()"
                  (click)="togglePhase3StatusRadio(Completed)"
                />
                <label class="form-check-label" for="phase3StatusCompleted">Completed</label>
              </div>
              <div class="form-check" [title]="phaseResolveDisabledReason() ?? ''">
                <input
                  id="phase3StatusNotNeeded"
                  class="form-check-input"
                  type="radio"
                  name="phase3PermitPhaseStatus"
                  [checked]="phase3Status() === NotNeeded"
                  [disabled]="!canResolveCurrentPhase()"
                  (click)="togglePhase3StatusRadio(NotNeeded)"
                />
                <label class="form-check-label" for="phase3StatusNotNeeded">Not Needed</label>
              </div>
            </div>
          </fieldset>
        }
      </div>
    </div>
    }
    </div>

    @if (saveError(); as err) {
      <div class="alert alert-danger mt-3 mb-0 small">{{ err }}</div>
    }
  </groundworks-modal-body>
  <groundworks-modal-footer [actions]="footerActions()" />
</groundworks-modal>
